import React, { useEffect, useState, useMemo } from "react";
import api from "../api/axiosConfig";
import { useAuth } from '../context/AuthContext';
import Layout from "../components/Layout";
import { PencilIcon } from '@heroicons/react/24/outline';

const RESTAURANT_ROLES = [
  { value: "waiter",  label: "Waiter" },
  { value: "cashier", label: "Cashier" },
  { value: "boh",     label: "Back of the House" },
  { value: "admin",   label: "Admin Staff" },
];

const BILLING_ROLES = [
  { value: "admin",      label: "Admin" },
  { value: "billing_ff", label: "Freight Forwarding Encoder" },
  { value: "accountant", label: "Accountant" },
  { value: "encoder",    label: "Encoder" },
];

const defaultFormState = {
  name: "",
  username: "",
  password: "",
  pin: "",
  employeeId: "",
  role: "waiter",
  stores: [],
  isActive: true,
};

const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [contextItems, setContextItems] = useState([]); // stores or accounting clients
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState(defaultFormState);
  const [submitted, setSubmitted] = useState(false);
  const { isAuthenticated, getAccessToken, user } = useAuth();
  const [tenantType, setTenantType] = useState(user?.tenantType || null);

  // Fetch tenantType from /me as a reliable fallback for sessions
  // issued before tenantType was added to the refresh token payload.
  useEffect(() => {
    if (!isAuthenticated || !getAccessToken()) return;
    if (tenantType) return; // already known from JWT
    api.get('/me').then((res) => {
      const tt = res.data?.tenant?.tenantType;
      if (tt) setTenantType(tt);
    }).catch(() => {});
  }, [isAuthenticated]);

  const isBilling = tenantType === 'billing';
  const roleOptions = isBilling ? BILLING_ROLES : RESTAURANT_ROLES;
  const defaultRole = isBilling ? 'admin' : 'waiter';

  const isSingleClientRole = formData.role === 'billing_ff';

  const contextLabel = isBilling ? 'Assigned Client' : 'Assigned Stores';

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated || !getAccessToken()) return;
      try {
        const empRes = await api.get('/employee');

        let items = [];
        if (isBilling) {
          const clientRes = await api.get('/accounting-clients?limit=all');
          items = clientRes.data?.data || [];
        } else {
          const storeRes = await api.get('/store');
          items = storeRes.data?.data || storeRes.data || [];
        }

        setEmployees(empRes.data);
        setContextItems(items);
      } catch (err) {
        setError(err.response?.data?.message || "Error fetching data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAuthenticated, isBilling]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      // When switching to billing_ff, cap stores to 1
      if (name === 'role' && value === 'billing_ff' && next.stores.length > 1) {
        next.stores = [next.stores[0]];
      }
      return next;
    });
  };

  const toggleEmployeeStatus = async (employeeId) => {
    try {
      await api.put(`/employee/${employeeId}/deactivate`, {});
      const res = await api.get('/employee');
      setEmployees(res.data);
    } catch (err) {
      setError(err.response?.data?.message || "Error updating employee status");
    }
  };

  const openAddModal = () => {
    setSelectedEmployee(null);
    setFormData({ ...defaultFormState, role: defaultRole });
    setSubmitted(false);
    setModalOpen(true);
  };

  const openEditModal = (employee) => {
    setSelectedEmployee(employee);
    setFormData({
      name: employee.name || "",
      username: employee.username || "",
      password: "",
      pin: "",
      employeeId: employee.employeeId || "",
      role: employee.role || defaultRole,
      stores: Array.isArray(employee.stores)
        ? employee.stores.map((s) => (typeof s === "object" ? s._id?.toString() : String(s)))
        : [],
      isActive: employee.isActive !== undefined ? employee.isActive : true,
    });
    setSubmitted(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedEmployee(null);
    setSubmitted(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    const trimmedName = formData.name.trim();
    const trimmedUsername = formData.username.trim();

    if (!trimmedName || !trimmedUsername) return;
    if (!selectedEmployee && !formData.password) return;
    if (!isBilling && !selectedEmployee && !formData.pin) return;

    try {
      const payload = {
        name: trimmedName,
        username: trimmedUsername,
        role: formData.role,
        employeeId: formData.employeeId,
        stores: formData.stores,
        isActive: formData.isActive,
      };
      if (formData.password) payload.password = formData.password;
      if (formData.pin) payload.pin = formData.pin;

      if (selectedEmployee) {
        await api.put(`/employee/${selectedEmployee._id}`, payload);
      } else {
        await api.post('/employee', payload);
      }

      const res = await api.get('/employee');
      setEmployees(res.data);
      closeModal();
    } catch (err) {
      setError(err.response?.data?.message || "Error saving employee");
    }
  };

  return (
    <Layout pageTitle="User Management">
      <div className="bg-gray-100 mx-auto p-6">
        {error && <div className="bg-red-200 text-red-800 p-3 mb-6 rounded">{error}</div>}

        <button onClick={openAddModal} className="transition-all duration-300 mb-4 px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600">
          + Add User
        </button>

        {loading ? (
          <div className="flex justify-center items-center h-32">
            <svg className="animate-spin h-10 w-10 text-teal-500" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          </div>
        ) : (
          <table className="min-w-full bg-white border border-gray-200 text-left">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b">Name</th>
                <th className="py-2 px-4 border-b">Username</th>
                <th className="py-2 px-4 border-b">Role</th>
                <th className="py-2 px-4 border-b">Status</th>
                <th className="py-2 px-4 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((employee) => (
                <tr key={employee._id} className="transition-all duration-300 hover:bg-slate-100">
                  <td className="py-2 px-4 border-b">{employee.name}</td>
                  <td className="py-2 px-4 border-b">{employee.username}</td>
                  <td className="py-2 px-4 border-b">{employee.role}</td>
                  <td className="py-2 px-4 border-b">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${employee.isActive ? "bg-green-500 text-white" : "bg-gray-400 text-white"}`}>
                      {employee.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="py-2 px-4 border-b gap-2 flex items-center">
                    <button onClick={() => openEditModal(employee)} className="transition-all duration-300 bg-teal-500 hover:bg-teal-600 text-white py-1 px-3 rounded mr-2">
                      <PencilIcon className="h-7 w-6" />
                    </button>
                    <button
                      onClick={() => toggleEmployeeStatus(employee._id)}
                      className={`py-1 mb-1 px-3 rounded ${employee.isActive ? "outline border-b-red-600 border-1 text-red-700 hover:bg-red-600 hover:text-slate-100" : "outline text-teal-500 hover:bg-teal-600 hover:text-teal-50"}`}
                    >
                      {employee.isActive ? "Deactivate" : "Activate"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {modalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded shadow-lg w-96 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-semibold mb-4">
                {selectedEmployee ? "Edit User" : "Add User"}
              </h2>

              <form onSubmit={handleSubmit}>
                {/* Name */}
                <label className="block mb-2 text-xs">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded mb-2 ${submitted && !formData.name.trim() ? "border-red-500" : ""}`}
                  required
                />
                {submitted && !formData.name.trim() && <p className="text-red-500 text-sm mb-2">Name is required</p>}

                {/* Username */}
                <label className="block mb-2 text-xs">Username</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded mb-2 ${submitted && !formData.username.trim() ? "border-red-500" : ""}`}
                  required
                />
                {submitted && !formData.username.trim() && <p className="text-red-500 text-sm mb-2">Username is required</p>}

                {/* Employee ID */}
                <label className="block mb-2 text-xs">Employee ID</label>
                <input
                  type="text"
                  name="employeeId"
                  value={formData.employeeId}
                  onChange={handleChange}
                  className="w-full p-2 border rounded mb-2"
                />

                {/* Password (new users only) */}
                {!selectedEmployee && (
                  <>
                    <label className="block mb-2 text-xs">Password</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`w-full p-2 border rounded mb-2 ${submitted && !formData.password ? "border-red-500" : ""}`}
                      required
                    />
                    {submitted && !formData.password && <p className="text-red-500 text-sm mb-2">Password is required</p>}
                  </>
                )}

                {/* PIN — required for restaurant new users, optional for billing */}
                <label className="block mb-2 text-xs">
                  PIN {isBilling ? <span className="text-gray-400 font-normal">(optional)</span> : null}
                </label>
                <input
                  type="password"
                  name="pin"
                  value={formData.pin}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded mb-2 ${submitted && !isBilling && !selectedEmployee && !formData.pin ? "border-red-500" : ""}`}
                  required={!isBilling && !selectedEmployee}
                />
                {submitted && !isBilling && !selectedEmployee && !formData.pin && (
                  <p className="text-red-500 text-sm mb-2">PIN is required</p>
                )}

                {/* Role */}
                <label className="block mb-2 text-xs">Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full p-2 border rounded mb-2"
                  required
                >
                  {roleOptions.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>

                {/* Store / Client assignment */}
                <label className="block mb-2 text-xs">
                  {contextLabel}
                  {isSingleClientRole && <span className="text-amber-600 ml-1">(select one)</span>}
                </label>

                {isSingleClientRole ? (
                  /* billing_ff: single radio — one client only */
                  <div className="grid grid-cols-1 gap-1 mb-4 max-h-36 overflow-y-auto border rounded p-2">
                    {contextItems.map((item) => {
                      const id = item._id?.toString();
                      return (
                        <label key={id} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="billingClientRadio"
                            value={id}
                            checked={formData.stores[0] === id}
                            onChange={() => setFormData((prev) => ({ ...prev, stores: [id] }))}
                            className="h-4 w-4 text-teal-600 border-gray-300 focus:ring focus:ring-teal-500"
                          />
                          <span className="text-gray-700 text-sm">
                            {item.name}
                            {item.clientCode ? <span className="text-gray-400 ml-1 text-xs">({item.clientCode})</span> : null}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  /* all other roles: multi-select checkboxes */
                  <div className="grid grid-cols-2 gap-2 mb-4 max-h-36 overflow-y-auto border rounded p-2">
                    {contextItems.map((item) => {
                      const id = item._id?.toString();
                      const isChecked = formData.stores.includes(id);
                      return (
                        <label key={id} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            value={id}
                            checked={isChecked}
                            onChange={() => {
                              setFormData((prev) => {
                                const updated = isChecked
                                  ? prev.stores.filter((s) => s !== id)
                                  : [...prev.stores, id];
                                return { ...prev, stores: updated };
                              });
                            }}
                            className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring focus:ring-teal-500"
                          />
                          <span className="text-gray-700 text-sm">
                            {item.name}
                            {item.clientCode ? <span className="text-gray-400 ml-1 text-xs">({item.clientCode})</span> : null}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}

                <div className="mt-4 flex justify-end space-x-2">
                  <button type="submit" className="px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600">
                    Save
                  </button>
                  <button type="button" onClick={closeModal} className="px-4 py-2 outline border-b-teal-700 text-teal-700 rounded hover:bg-slate-600 hover:text-slate-50">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default EmployeeManagement;
